import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const STEP_PARAM = 'step';

function parseStep(searchParams: URLSearchParams, totalSteps: number): number {
  const parsed = parseInt(searchParams.get(STEP_PARAM) || '1', 10);
  if (isNaN(parsed) || parsed < 1) return 1;
  if (parsed > totalSteps) return totalSteps;
  return parsed;
}

export function useQuizNavigation(totalSteps: number) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(() => parseStep(searchParams, totalSteps));

  useEffect(() => {
    setStep(parseStep(searchParams, totalSteps));
  }, [searchParams, totalSteps]);

  const updateUrl = useCallback(
    (newStep: number, replace: boolean) => {
      const params = new URLSearchParams(searchParams);
      if (newStep <= 1) {
        params.delete(STEP_PARAM);
      } else {
        params.set(STEP_PARAM, String(newStep));
      }
      setSearchParams(params, { replace });
    },
    [searchParams, setSearchParams]
  );

  const goNext = useCallback(() => {
    const newStep = Math.min(step + 1, totalSteps);
    updateUrl(newStep, false);
  }, [step, totalSteps, updateUrl]);

  const goBack = useCallback(() => {
    if (step > 1) {
      window.history.back();
    }
  }, [step]);

  return {
    step,
    goNext,
    goBack,
    canGoBack: step > 1,
  };
}
